import type { ResolvedTheme } from '../theme'

interface Props {
  theme: ResolvedTheme
}

export function frameShellStyle(theme: ResolvedTheme): React.CSSProperties | undefined {
  const inset = theme.board.frameInset
  if (!inset) return undefined
  return {
    '--board-left': `${inset.left * 100}%`,
    '--board-top': `${inset.top * 100}%`,
    '--board-width': `${(1 - inset.left - inset.right) * 100}%`,
    '--board-height': `${(1 - inset.top - inset.bottom) * 100}%`,
  } as React.CSSProperties
}

/**
 * Thick cabinet art cannot live inside the board-sized Pixi canvas: there it
 * would cover the outside symbols or be clipped. This DOM overscan layer keeps
 * the art's declared transparent opening locked to the canvas at every scale.
 */
export function OuterBoardFrame({ theme }: Props): React.JSX.Element | null {
  const image = theme.board.frameImage
  const inset = theme.board.frameInset
  if (!image || !inset) return null
  const opening = theme.board.frameOpeningInset
  const gutterStyle: React.CSSProperties | undefined = opening
    ? {
        left: `${opening.left * 100}%`,
        right: `${opening.right * 100}%`,
        top: `${opening.top * 100}%`,
        bottom: `${opening.bottom * 100}%`,
        // A textured board may carry shaped alpha (for example rounded
        // corners). Do not paint an opaque rectangular colour beneath it,
        // otherwise that colour leaks past the decorative frame cutout.
        backgroundColor:
          theme.board.backgroundImage || theme.board.background == null
            ? undefined
            : `#${theme.board.background.toString(16).padStart(6, '0')}`,
        backgroundImage: theme.board.backgroundImage
          ? `url("${theme.board.backgroundImage}")`
          : undefined,
      }
    : undefined

  return (
    <>
      {gutterStyle && <span className="board__frame-gutter" aria-hidden="true" style={gutterStyle} />}
      <img className="board__outer-frame" src={image} alt="" aria-hidden="true" />
    </>
  )
}
